import { EventBus } from '@pp/toolkit';

export enum PressureState {
  Nominal = 'nominal',
  Fair = 'fair',
  Serious = 'serious',
  Critical = 'critical',
}

export enum PressureObserverErrorReason {
  NotAllowed = 'not-allowed',
  NotSupported = 'not-supported',
  Unknown = 'unknown',
}

export class PressureTracker extends EventBus<{
  observerError: (source: string, reason: PressureObserverErrorReason) => void;
}> {
  cpuPressure:
    | {
        [PressureState.Nominal]: number;
        [PressureState.Fair]: number;
        [PressureState.Serious]: number;
        [PressureState.Critical]: number;
      }
    | undefined;

  constructor() {
    super();
    if (!window.PressureObserver || !PressureObserver.knownSources?.includes('cpu')) return;
    try {
      const handleRecords = (changes: Array<PressureRecord>): void => {
        changes.forEach((record) => {
          this.cpuPressure ??= {
            [PressureState.Nominal]: 0,
            [PressureState.Fair]: 0,
            [PressureState.Serious]: 0,
            [PressureState.Critical]: 0,
          };
          if (record.source === 'cpu') {
            this.cpuPressure[record.state] += 1;
          }
        });
      };

      const observer = new PressureObserver(handleRecords);

      observer
        .observe('cpu', {
          sampleInterval: 1000,
        })
        .catch((error) => {
          let reason: PressureObserverErrorReason = PressureObserverErrorReason.Unknown;
          if (error?.name === 'NotAllowedError') {
            reason = PressureObserverErrorReason.NotAllowed;
          } else if (error?.name === 'NotSupportedError') {
            reason = PressureObserverErrorReason.NotSupported;
          }
          this.dispatchEvent('observerError', 'cpu', reason);
        });
    } catch (error) {
      //
    }
  }

  getCpuPressureDistributionAsInt32(): number {
    if (!this.cpuPressure) {
      return 0;
    }

    const total = Object.values(this.cpuPressure).reduce((acc, val) => acc + val, 0);

    const normalizedValues: [number, number, number, number] = [
      this.normalize8Bit(this.cpuPressure[PressureState.Nominal], total),
      this.normalize8Bit(this.cpuPressure[PressureState.Fair], total),
      this.normalize8Bit(this.cpuPressure[PressureState.Serious], total),
      this.normalize8Bit(this.cpuPressure[PressureState.Critical], total),
    ];

    return (
      ((normalizedValues[3] << 24) | (normalizedValues[2] << 16) | (normalizedValues[1] << 8) | normalizedValues[0]) >>>
      0
    );
  }

  normalize8Bit(value: number, total: number): number {
    if (total === 0) return 0; // Avoid division by zero
    return Math.min(255, Math.round((value / total) * 255));
  }
}
